import React, { useRef, useState } from 'react';
import { MessageSquare, Paperclip, Flag, ChevronDown, Plus } from 'lucide-react';
import Badge from '../../../common/Badge';
import FormControl from '../../../common/FormControl';
import Skeleton from '../../../common/Skeleton';
import { useEntitySettings } from '../../../../contexts/EntitySettingsContext';

/**
 * SeamlessKit — shared primitives for the execution surface. Each item is a
 * self-contained CARD split into two columns by a vertical divider:
 *   • identity (left)  — id · title · type chip, with the note/evidence/findings
 *                        action row anchored at the bottom;
 *   • assessment (right) — a tinted panel of stacked, labeled score rows
 *                        (Existence/Effectiveness · Likelihood/Impact) or status
 *                        buttons, with a verdict pill below.
 * Implementation steps render full-width inside the card. Presentation only —
 * all persistence stays in the hosting response body.
 */

/* ---- surface ------------------------------------------------------------- */
export const SeamlessSurface: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={`seamless${className ? ` ${className}` : ''}`}>{children}</div>
);

/* ---- card ---------------------------------------------------------------- */
export const SeamlessCard: React.FC<{
  code?: string | null;
  sno: number;
  /** Overrides the rendered serial (e.g. "R1", "C1"). Falls back to `${sno}.`. */
  snoLabel?: string;
  /** Tints the card border — subprocess (black) / risk (red) / control (green). */
  variant?: 'subprocess' | 'risk' | 'control';
  title: string;
  sub?: string | null;           // optional muted line under the title
  /** Phase 113 REQ-9 — inline remark (evidence_text / rationale) rendered in
   *  light grey below the identity line. Pass a `.response-ra-inline-remark`
   *  div or any node; null/undefined renders nothing. */
  inlineRemark?: React.ReactNode;
  typeChip?: React.ReactNode;
  /** Phase 113 — small grey icon cluster rendered immediately after the title
   *  (and typeChip/code) inside seamless-card__id-line, on the LEFT side.
   *  Pass the same `.response-action-cluster.response-action-cluster--inline`
   *  div used by the table bodies (💬 note, 📎 evidence, 🚩 finding icons). */
  inlineActions?: React.ReactNode;
  actions?: React.ReactNode;     // ResponseBar
  assessment: React.ReactNode;   // AssessmentPanel
  impl?: React.ReactNode;        // ImplList (Control Test) or evidence block
  footer?: React.ReactNode;      // RejectNoteCell
}> = ({ code, sno, snoLabel, variant, title, sub, inlineRemark, typeChip, inlineActions, actions, assessment, impl, footer }) => (
  <div className={`seamless-card${variant ? ` seamless-card--${variant}` : ''}`}>
    <div className="seamless-card__main">
      <div className="seamless-card__identity">
        <div className="seamless-card__id-line">
          <span className="seamless-card__sno">{snoLabel ?? `${sno}.`}</span>
          <span className="seamless-card__title">{title}</span>
          {sub && <span className="seamless-card__sub">: {sub}</span>}
          {typeChip}
          {code && <span className="seamless-card__code">{code}</span>}
          {inlineActions && (
            <span className="seamless-card__inline-actions">{inlineActions}</span>
          )}
        </div>
        {inlineRemark}
      </div>
      <div className="seamless-card__divider" />
      <div className="seamless-card__assess-col">
        {assessment}
        {actions && <div className="seamless-card__bar">{actions}</div>}
      </div>
    </div>
    {impl && <div className="seamless-card__impl">{impl}</div>}
    {footer}
  </div>
);

/* ---- type chip (a hue per control type) ---------------------------------- */
const TYPE_VARIANT: Record<string, 'info' | 'warning' | 'neutral' | 'success' | 'danger'> = {
  preventive: 'info',
  detective: 'warning',
  corrective: 'neutral',
};
export const TypeChip: React.FC<{ label?: string | null }> = ({ label }) => {
  if (!label) return null;
  const v = TYPE_VARIANT[label.toLowerCase()] ?? 'neutral';
  return <Badge variant={v} size="xs">{label}</Badge>;
};

/* ---- assessment panel ---------------------------------------------------- */
export const AssessmentPanel: React.FC<{ children: React.ReactNode; verdict?: React.ReactNode }> = ({
  children, verdict,
}) => (
  <div className="seamless-assess">
    <div className="seamless-assess__scores">{children}</div>
    {verdict && <div className="seamless-assess__verdict">{verdict}</div>}
  </div>
);

/* ---- dual-row scoring panel (Inherent + Control → Residual) --------------- */
/* Same box width as AssessmentPanel, but TWO score lines each with its own
   verdict pill (e.g. Inherent on row 1, Residual on row 2). Pills are
   label-less and equal-width; the label shows on hover via NetScore.title. */
export const DualScorePanel: React.FC<{
  row1: React.ReactNode; verdict1: React.ReactNode;
  row2: React.ReactNode; verdict2: React.ReactNode;
}> = ({ row1, verdict1, row2, verdict2 }) => (
  <div className="seamless-assess seamless-assess--dual">
    <div className="seamless-assess__line">
      <div className="seamless-assess__scores">{row1}</div>
      <div className="seamless-assess__verdict">{verdict1}</div>
    </div>
    <div className="seamless-assess__line">
      <div className="seamless-assess__scores">{row2}</div>
      <div className="seamless-assess__verdict">{verdict2}</div>
    </div>
  </div>
);

/* ---- assessment score row (label + modern slider + value) ---------------- */
/* Semantic slider fill — discrete 5-stop risk-heatmap palette (the standard
   GRC green→amber→red ramp), not a continuous hue sweep (which muddied to olive
   at mid values). Index by the value ratio.
   risk    — higher is worse: green → red as the value rises.
   control — higher is better: red → green as the value rises. */
const RISK_RAMP = ['#22c55e', '#84cc16', '#eab308', '#f97316', '#ef4444']; // low→high
const toneFill = (ratio: number, tone: 'risk' | 'control'): string => {
  const r = Math.max(0, Math.min(1, ratio));
  const i = Math.min(RISK_RAMP.length - 1, Math.max(0, Math.ceil(r * RISK_RAMP.length) - 1));
  const ramp = tone === 'risk' ? RISK_RAMP : [...RISK_RAMP].reverse();
  return ramp[i];
};

export const AssessRow: React.FC<{
  label: string;
  /** Full name shown on hover when `label` is an abbreviation (e.g. Ex → Existence). */
  labelTitle?: string;
  value: number | '';
  min?: number;
  max?: number;
  disabled?: boolean;
  /** Semantic fill direction. Omit → flat brand color. */
  tone?: 'risk' | 'control';
  onChange: (v: number | '') => void;
  testId?: string;
}> = ({ label, labelTitle, value, min = 0, max = 5, disabled, tone, onChange, testId }) => {
  const set = value !== '' && value != null;
  const ratio = set ? Number(value) / max : 0;
  const pct = ratio * 100;
  const toned = tone && set;
  // Read-only: drop the semantic colour and render the bar in a muted grey so it
  // reads as a locked, static gauge (not a live, grabbable slider).
  const fill = disabled ? 'var(--gray-400)' : toned ? toneFill(ratio, tone!) : 'var(--brand)';
  return (
    <div className="seamless-assess__field">
      <span className="seamless-assess__label" title={labelTitle ?? label}>{label}</span>
      <input
        type="range"
        className="seamless-range"
        min={min}
        max={max}
        step={1}
        value={set ? (value as number) : min}
        disabled={disabled}
        data-testid={testId}
        style={{
          background: `linear-gradient(to right, ${fill} ${pct}%, var(--gray-200) ${pct}%)`,
          // drives the thumb (dot) color via the slider pseudo-elements
          ['--seamless-fill' as any]: fill,
        }}
        onChange={(e) => {
          const v = Number(e.target.value);
          // 0 is a legitimate score where the scale starts at 0 — an absent
          // control is exactly what an assessment most often finds. This
          // coerced it to 'unset', so a genuine zero could not be recorded
          // and the completeness gate could not tell the two apart.
          onChange(v);
        }}
      />
      <span
        className={`seamless-assess__val${set ? ' seamless-assess__val--set' : ''}`}
        style={toned && !disabled ? { color: fill } : undefined}
      >{set ? value : min}</span>
    </div>
  );
};

/* ---- loading skeleton (mirrors the real card chrome) --------------------- */
export const SeamlessSkeleton: React.FC<{ rows?: number }> = ({ rows = 3 }) => (
  <div className="seamless" aria-busy="true" aria-live="polite">
    {Array.from({ length: rows }, (_, i) => (
      <div key={i} className="seamless-card" aria-hidden="true">
        <div className="seamless-card__main">
          <div className="seamless-card__identity">
            <div className="seamless-card__id-line"><Skeleton.Box height={15} width={240} /></div>
            <div className="seamless-card__actions"><Skeleton.Box height={28} width={200} radius={8} /></div>
          </div>
          <div className="seamless-card__divider" />
          <div className="seamless-card__assess-col">
            <div className="seamless-assess">
              <div className="seamless-assess__row"><Skeleton.Box height={14} width="100%" /></div>
              <div className="seamless-assess__row"><Skeleton.Box height={14} width="100%" /></div>
              <div className="seamless-assess__sep" />
              <div className="seamless-assess__verdict"><Skeleton.Box height={18} width={64} radius={7} /></div>
            </div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

/* ---- verdict / net-score pill -------------------------------------------- */
export type ScoreVariant = 'none' | 'success' | 'warning' | 'danger';

/* Score → variant bands (the product's risk-tolerance policy, in one place).
   scoreBand: net out of `max` (existence×effectiveness, 25). riskBand: raw
   likelihood×severity (25). */
export const scoreBand = (net: number | null | undefined, max = 25): ScoreVariant => {
  if (net == null) return 'none';
  const r = net / max;
  return r >= 0.72 ? 'success' : r >= 0.4 ? 'warning' : 'danger';
};
export const riskBand = (score: number | null | undefined): ScoreVariant =>
  score == null ? 'none' : score <= 4 ? 'success' : score >= 13 ? 'danger' : 'warning';
export const NetScore: React.FC<{ text: string; variant: ScoreVariant; title?: string; testId?: string; emphasis?: boolean }> = ({
  text, variant, title, testId, emphasis,
}) => (
  <span
    className={`seamless__net seamless__net--${variant}${emphasis ? ' seamless__net--emph' : ''}`}
    title={title}
    data-testid={testId}
  >{text}</span>
);

export const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="seamless__section-label">{children}</div>
);

/* ---- icon chip ----------------------------------------------------------- */
const IconChip: React.FC<{
  Ico: React.ComponentType<any>;
  label?: string;
  count?: number;
  filled?: boolean;
  tone?: 'danger';
  active?: boolean;
  chevron?: boolean;
  open?: boolean;
  disabled?: boolean;
  title?: string;
  testId?: string;
  onClick: () => void;
}> = ({ Ico, label, count, filled, tone, active, chevron, open, disabled, title, testId, onClick }) => {
  const hasCount = typeof count === 'number' && count > 0;
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      title={title || label}
      data-testid={testId}
      className={`seamless-chip${label ? '' : ' seamless-chip--icon-only'}${active ? ' seamless-chip--active' : ''}`}
    >
      <span className="seamless-chip__ico">
        <Ico size={15} />
        {filled && <span className="seamless-chip__dot" />}
      </span>
      {label && <span>{label}</span>}
      {hasCount && (
        <span className={`seamless-chip__badge${tone === 'danger' ? ' seamless-chip__badge--danger' : ''}`}>{count}</span>
      )}
      {chevron && <ChevronDown size={14} className={`seamless-chip__chev${open ? ' seamless-chip__chev--open' : ''}`} />}
    </button>
  );
};

/* ---- compact implementation rows ----------------------------------------- */
export const ImplList: React.FC<{
  items: Array<{ id: number; sno?: number | null; title: string; sub?: string | null; result: string; observations: string }>;
  onResult: (id: number, result: string) => void;
  onObservations: (id: number, text: string) => void;
  onObservationsBlur?: (id: number, text: string) => void;
  /** When set, the observation icon opens a POPUP (no inline textarea). */
  onOpenObservation?: (id: number) => void;
  disabled?: boolean;
  testIdPrefix?: string;
  /** Heading for the inline observation text. Defaults to "Remarks:" so
   *  existing bodies are unchanged; Risk Assessment passes "Observation:". */
  observationLabel?: string;
}> = ({ items, onResult, onObservations, onObservationsBlur, onOpenObservation, disabled, testIdPrefix = 'impl', observationLabel = 'Remarks:' }) => {
  const [openObs, setOpenObs] = useState<number | null>(null);
  const RESULTS: Array<[string, 'pass' | 'fail' | 'na']> = [['Pass', 'pass'], ['Fail', 'fail'], ['NA', 'na']];
  return (
    <div>
      {items.map((r) => {
        const open = openObs === r.id;
        return (
          <div key={r.id} className="seamless__impl">
            <div className="seamless__impl-row">
              <span className="seamless__impl-sno">{r.sno ?? ''}</span>
              <div className="seamless__impl-main">
                <div className="seamless__impl-title-row">
                  <span className="seamless__impl-title">{r.title}</span>
                  {/* Note icon — same button recipe as the control/question remark cluster. */}
                  <button
                    type="button"
                    className="response-row-action seamless__impl-note"
                    aria-label="Add remark"
                    title={r.observations ? 'Edit observation' : 'Add observation'}
                    disabled={disabled}
                    onClick={() => (onOpenObservation ? onOpenObservation(r.id) : setOpenObs(open ? null : r.id))}
                  >
                    <MessageSquare size={12} />
                  </button>
                </div>
              </div>
              <div className="seamless__impl-actions">
                <div className="collapse-section__result-group" role="group" style={{ display: 'flex', gap: 8 }}>
                  {RESULTS.map(([val, mod]) => {
                    const on = r.result === val;
                    return (
                      <button
                        key={val}
                        type="button"
                        disabled={disabled}
                        aria-pressed={on}
                        onClick={() => onResult(r.id, on ? '' : val)}
                        className={`collapse-section__result-btn${on ? ` collapse-section__result-btn--${mod}` : ''}`}
                        data-testid={`BTN_${testIdPrefix}_result_${r.id}_${val.toLowerCase()}`}
                      >
                        {val}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            {/* Observation shown as "<label> • text" below the row — label defaults
                to "Remarks:" (all bodies); Risk Assessment overrides to "Observation:". */}
            {r.observations && (
              <div className="response-qmeta">
                <div className="response-qmeta__section">
                  <span className="response-qmeta__label">{observationLabel}</span>
                  <button
                    type="button"
                    className="response-qmeta__item response-qmeta__remark response-qmeta__remark--clickable"
                    disabled={disabled}
                    onClick={() => (onOpenObservation ? onOpenObservation(r.id) : setOpenObs(open ? null : r.id))}
                  >
                    <span>{r.observations}</span>
                  </button>
                </div>
              </div>
            )}
            {!onOpenObservation && open && (
              <div className="seamless__impl-obs">
                <FormControl.Textarea
                  label=""
                  value={r.observations}
                  rows={2}
                  noMargin
                  placeholder="Observation (optional)"
                  disabled={disabled}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onObservations(r.id, e.target.value)}
                  onBlur={(e: React.FocusEvent<HTMLTextAreaElement>) => onObservationsBlur?.(r.id, e.target.value)}
                  autoFocus
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/* ---- status segmented buttons -------------------------------------------- */
export const StatusButtons: React.FC<{
  options: Array<{ val: string; label: string; mod: 'pass' | 'fail' | 'na' }>;
  value: string;
  onChange: (val: string) => void;
  disabled?: boolean;
  testId?: (val: string) => string | undefined;
}> = ({ options, value, onChange, disabled, testId }) => (
  <div className="collapse-section__result-group" role="group" aria-label="Result" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
    {options.map((o) => {
      const on = value === o.val;
      return (
        <button
          key={o.val}
          type="button"
          disabled={disabled}
          aria-pressed={on}
          onClick={() => onChange(on ? '' : o.val)}
          className={`collapse-section__result-btn${on ? ` collapse-section__result-btn--${o.mod}` : ''}`}
          data-testid={testId?.(o.val)}
        >
          {o.label}
        </button>
      );
    })}
  </div>
);

/* ---- response bar: note / evidence(1-click) / findings ------------------- */
export interface ChipSlot {
  label: string;
  filled?: boolean;
  count?: number;
  tone?: 'danger';
  /** Inline-expand content. Omit when using `onClick` (popup mode). */
  render?: () => React.ReactNode;
  /** When set, the chip opens a POPUP (no inline expand) — like Checklist remarks. */
  onClick?: () => void;
}
export interface EvidenceSlot {
  label: string;
  count?: number;
  attachedName?: string;
  disabled?: boolean;
  accept?: string;
  onPick: (file: File) => void;
  /** When evidence is attached and this is set, clicking opens it (instead of the
   *  picker). Allowed even when `disabled` (read-only) so reviewers can view. */
  onView?: () => void;
  /** When set, the chip OPENS THE MULTI-EVIDENCE POPUP on every click (list +
   *  upload + view/detach), bypassing onPick/onView. Always clickable, even
   *  read-only (the popup enforces read-only on writes). Takes precedence. */
  onOpen?: () => void;
}
/* findings/deficiencies — a flag icon that opens a POPUP (like every other
   procedure's RaiseFindingButton), NOT an inline dropdown. */
export interface FindingsSlot {
  label: string;          // tooltip only (chips are icon-only)
  count?: number;
  tone?: 'danger';
  onClick: () => void;
}

const EvidenceChip: React.FC<{ slot: EvidenceSlot }> = ({ slot }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const hasFile = !!slot.attachedName || (slot.count ?? 0) > 0;
  const canView = hasFile && !!slot.onView;
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={slot.accept}
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) slot.onPick(f);
          e.currentTarget.value = '';
        }}
      />
      <IconChip
        Ico={Paperclip}
        count={slot.count}
        active={!!slot.attachedName || (slot.count ?? 0) > 0}
        // Popup mode is always clickable; otherwise keep clickable for the view
        // action even when read-only and only block upload.
        disabled={slot.onOpen ? false : (slot.disabled && !canView)}
        title={
          slot.onOpen
            ? 'Evidence — view & attach'
            : canView
              ? `View evidence${slot.attachedName ? `: ${slot.attachedName}` : ''}`
              : slot.attachedName ? `Attached: ${slot.attachedName}` : slot.label
        }
        onClick={() => {
          if (slot.onOpen) { slot.onOpen(); return; }
          if (canView) slot.onView!();
          else if (!slot.disabled) inputRef.current?.click();
        }}
      />
    </>
  );
};

/* Icon-only chips. Note still expands inline (it's a text field); evidence picks
   a file; findings opens a popup. */
export const ResponseBar: React.FC<{
  note?: ChipSlot;
  evidence?: EvidenceSlot;
  findings?: FindingsSlot;
}> = ({ note, evidence, findings }) => {
  const [noteOpen, setNoteOpen] = useState(false);
  return (
    <div>
      <div className="seamless__bar">
        {note && (
          <IconChip
            Ico={MessageSquare}
            title={note.label}
            filled={note.filled}
            active={!note.onClick && noteOpen}
            onClick={note.onClick ?? (() => setNoteOpen((o) => !o))}
          />
        )}
        {evidence && <EvidenceChip slot={evidence} />}
        {findings && (
          <IconChip
            Ico={Flag}
            title={findings.label}
            count={findings.count}
            tone={findings.tone}
            onClick={findings.onClick}
          />
        )}
      </div>
      {!note?.onClick && noteOpen && note?.render && (
        <div className="seamless__expand">{note.render()}</div>
      )}
      {evidence?.attachedName && (
        <div className="seamless__attached">
          <Paperclip size={13} /> {evidence.attachedName}
        </div>
      )}
    </div>
  );
};

/* ---- findings list ------------------------------------------------------- */
export const FindingsInline: React.FC<{
  findings: Array<{ id: number | string; severity?: string | null; status?: string | null; desc?: string | null; detected?: string | null }>;
  word: string;
  onRaise?: () => void;
  onView?: (id: number | string) => void;
  disabled?: boolean;
}> = ({ findings, word, onRaise, onView, disabled }) => {
  const { formatDate } = useEntitySettings();
  return (
  <div className="seamless-findings">
    {findings.map((f) => {
      const sev = (f.severity || '').toLowerCase();
      const mod = sev === 'high' ? 'high' : sev === 'low' ? 'low' : 'medium';
      return (
        <div
          key={f.id}
          className="seamless-finding"
          onClick={onView ? () => onView(f.id) : undefined}
          style={onView ? { cursor: 'pointer' } : undefined}
        >
          <span className={`seamless-finding__dot seamless-finding__dot--${mod}`} />
          <span className="seamless-finding__desc">{f.desc || `(${word})`}</span>
          <span className="seamless-finding__meta">{[f.severity, f.status, f.detected ? formatDate(f.detected) : null].filter(Boolean).join(' · ')}</span>
        </div>
      );
    })}
    {onRaise && !disabled && (
      <div>
        <button type="button" className="seamless-finding__raise" onClick={onRaise}>
          <Plus size={13} /> Raise {word.toLowerCase()}
        </button>
      </div>
    )}
  </div>
  );
};

/* ---- findings adapters → FindingsInline item shape ----------------------- */
export interface FindingItem {
  id: number | string;
  severity?: string | null;
  status?: string | null;
  desc?: string | null;
  detected?: string | null;
}
/** audit findings rows (is_trivial/is_closed/findings_text/raised_on_date). */
export const mapAuditFindings = (
  rows: Array<{ id: number | string; is_trivial?: boolean; is_closed?: boolean; findings_text?: string | null; raised_on_date?: string | null }>
): FindingItem[] => rows.map((f) => ({
  id: f.id,
  severity: f.is_trivial ? 'Low' : 'Medium',
  status: f.is_closed ? 'Closed' : 'Open',
  desc: f.findings_text ?? null,
  detected: f.raised_on_date ?? null,
}));
/** deficiency / vulnerability rows (detected_severity/status/description/detected_on). */
export const mapDeficiencyFindings = (
  rows: Array<{ id: number | string; detected_severity?: string | null; status?: string | null; description?: string | null; detected_on?: string | null }>
): FindingItem[] => rows.map((d) => ({
  id: d.id,
  severity: d.detected_severity ?? null,
  status: d.status ?? null,
  desc: d.description ?? null,
  detected: d.detected_on ?? null,
}));
